import api from '../utils/api';

// Posts API
export const postsApi = {
  // Get all posts with filters
  getPosts: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    return api.get(`/intranet/posts?${params.toString()}`);
  },

  // Get single post
  getPost: (id) => api.get(`/intranet/posts/${id}`),

  // Create new post
  createPost: (postData) => api.post('/intranet/posts', postData),

  // Update post
  updatePost: (id, postData) => api.put(`/intranet/posts/${id}`, postData),

  // Delete post
  deletePost: (id) => api.delete(`/intranet/posts/${id}`),

  // Pin/unpin post
  togglePin: (id) => api.post(`/intranet/posts/${id}/pin`),

  // Like/unlike post
  toggleLike: (id) => api.post(`/intranet/posts/${id}/like`),

  // Get users who liked a post
  getPostLikes: (id) => api.get(`/intranet/posts/${id}/likes`),

  // Bookmark/unbookmark post
  toggleBookmark: (id) => api.post(`/intranet/posts/${id}/bookmark`),

  // Get user's bookmarked posts
  getUserBookmarks: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    return api.get(`/intranet/posts/bookmarks/my?${params.toString()}`);
  },

  // Get bookmark statistics for a post
  getPostBookmarkStats: (id) => api.get(`/intranet/posts/${id}/bookmarks`),

  // Get most bookmarked posts (popular posts)
  getPopularBookmarkedPosts: (limit = 10) => api.get(`/intranet/posts/bookmarks/popular?limit=${limit}`),

  // Acknowledge required reading
  acknowledgePost: (id) => api.post(`/intranet/posts/${id}/acknowledge`),

  // Acknowledge required reading (alternative method name for RequiredReading component)
  acknowledgeRequiredReading: (id) => api.post(`/intranet/posts/${id}/acknowledge`),

  // Get pending required reading for current user
  getRequiredReading: () => api.get('/intranet/posts/required-reading/pending'),

  // Get required reading count for current user
  getRequiredReadingCount: () => api.get('/intranet/posts/required-reading/count'),

  // Admin functions
  adminDeletePost: (id, reason) => api.delete(`/intranet/posts/${id}/admin`, { data: { reason } }),
  restorePost: (id) => api.post(`/intranet/posts/${id}/restore`)
};

// Comments API
export const commentsApi = {
  // Get comments for a post
  getComments: (postId) => api.get(`/intranet/posts/${postId}/comments`),

  // Add comment
  addComment: (postId, commentData) => api.post(`/intranet/posts/${postId}/comments`, commentData),

  // Update comment
  updateComment: (id, commentData) => api.put(`/intranet/posts/comments/${id}`, commentData),

  // Delete comment
  deleteComment: (id) => api.delete(`/intranet/posts/comments/${id}`),

  // Admin functions
  adminDeleteComment: (id, reason) => api.delete(`/intranet/posts/comments/${id}/admin`, { data: { reason } }),
  restoreComment: (id) => api.post(`/intranet/posts/comments/${id}/restore`),

  // Best answer functionality
  markAsBestAnswer: (postId, commentId) => api.post(`/intranet/posts/${postId}/comments/${commentId}/best-answer`),
  removeBestAnswer: (postId) => api.delete(`/intranet/posts/${postId}/best-answer`),
  getBestAnswer: (postId) => api.get(`/intranet/posts/${postId}/best-answer`)
};

// Events API
export const eventsApi = {
  // Get all events with filters
  getEvents: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    return api.get(`/intranet/events?${params.toString()}`);
  },

  // Get all events (alternative method name for CompanyEvents component)
  getAllEvents: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    return api.get(`/intranet/events?${params.toString()}`);
  },

  // Get upcoming events
  getUpcomingEvents: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    return api.get(`/intranet/events/upcoming?${params.toString()}`);
  },

  // Get events for calendar view
  getCalendarEvents: (startDate, endDate, departmentId = null) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (departmentId) params.append('department_id', departmentId);
    return api.get(`/intranet/events/calendar?${params.toString()}`);
  },

  // Get single event
  getEvent: (id) => api.get(`/intranet/events/${id}`),

  // Create new event
  createEvent: (eventData) => api.post('/intranet/events', eventData),

  // Update event
  updateEvent: (id, eventData) => api.put(`/intranet/events/${id}`, eventData),

  // Delete event
  deleteEvent: (id) => api.delete(`/intranet/events/${id}`),

  // RSVP to event
  rsvpToEvent: (id, rsvpData) => api.post(`/intranet/events/${id}/rsvp`, rsvpData),

  // Get event attendees
  getEventAttendees: (id, rsvpStatus = null) => {
    const params = rsvpStatus ? `?rsvp_status=${rsvpStatus}` : '';
    return api.get(`/intranet/events/${id}/attendees${params}`);
  }
};

// Points API
export const pointsApi = {
  // Get user leaderboard
  getLeaderboard: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    return api.get(`/intranet/points/leaderboard?${params.toString()}`);
  },

  // Get current user's points and rank
  getCurrentUserPoints: () => api.get('/intranet/points/me'),

  // Get user's points history
  getUserPoints: (userId = null, filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const url = userId ? `/intranet/points/user/${userId}` : '/intranet/points/user';
    return api.get(`${url}?${params.toString()}`);
  },

  // Get points summary
  getPointsSummary: (userId = null) => {
    const url = userId ? `/intranet/points/summary/${userId}` : '/intranet/points/summary';
    return api.get(url);
  },

  // Point overrides (admin only)
  createPointOverride: (overrideData) => api.post('/intranet/points/overrides', overrideData),
  getPointOverrides: (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    return api.get(`/intranet/points/overrides?${params.toString()}`);
  },
  getUserOverrideHistory: (userId, limit = 20) => api.get(`/intranet/points/overrides/user/${userId}?limit=${limit}`),
  getOverrideStats: () => api.get('/intranet/points/overrides/stats')
};

// Configuration API
export const configApi = {
  // Post categories
  getPostCategories: () => api.get('/intranet/config/categories'),
  createPostCategory: (categoryData) => api.post('/intranet/config/categories', categoryData),
  updatePostCategory: (id, categoryData) => api.put(`/intranet/config/categories/${id}`, categoryData),
  deletePostCategory: (id) => api.delete(`/intranet/config/categories/${id}`),

  // Event types
  getEventTypes: () => api.get('/intranet/config/event-types'),
  createEventType: (typeData) => api.post('/intranet/config/event-types', typeData),
  updateEventType: (id, typeData) => api.put(`/intranet/config/event-types/${id}`, typeData),
  deleteEventType: (id) => api.delete(`/intranet/config/event-types/${id}`),

  // Point settings (these are in the points routes, not config routes)
  getPointSettings: () => api.get('/intranet/points/settings'),
  updatePointSettings: (actionType, settingsData) => api.put(`/intranet/points/settings/${actionType}`, settingsData)
};

// Departments API
export const departmentsApi = {
  // Get all departments
  getDepartments: () => api.get('/departments'),
  
  // Create new department
  createDepartment: (departmentData) => api.post('/departments', departmentData),
  
  // Update department
  updateDepartment: (id, departmentData) => api.put(`/departments/${id}`, departmentData),
  
  // Delete department
  deleteDepartment: (id) => api.delete(`/departments/${id}`)
};

// Export all APIs
export default {
  posts: postsApi,
  comments: commentsApi,
  events: eventsApi,
  points: pointsApi,
  config: configApi,
  departments: departmentsApi
}; 